import { useState, useEffect, useCallback } from "react";
import {
  CONSENT_VERSION,
  type ConsentCategory,
  type ConsentRecord,
  getDefaultCategories,
  getConsent,
  saveConsent,
} from "@/lib/consent";

type View = "banner" | "settings" | "hidden";

export default function CookieConsentBanner() {
  const [view, setView] = useState<View>("hidden");
  const [categories, setCategories] = useState<
    Record<ConsentCategory, boolean>
  >(getDefaultCategories());

  // Check consent status on mount and listen for manual "open settings" events
  useEffect(() => {
    const checkConsent = () => {
      const existing = getConsent();
      if (!existing) {
        setView("banner");
        setCategories(getDefaultCategories());
      } else {
        setView("hidden");
      }
    };

    checkConsent();

    const openSettings = () => {
      const existing = getConsent();
      setCategories(existing?.categories ?? getDefaultCategories());
      setView("settings");
    };

    window.addEventListener("spyclean-open-consent-settings", openSettings);
    return () =>
      window.removeEventListener(
        "spyclean-open-consent-settings",
        openSettings,
      );
  }, []);

  const handleAcceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      statistics: true,
      marketing: true,
      external_media: true,
    });
    setView("hidden");
  }, []);

  const handleRejectAll = useCallback(() => {
    saveConsent(getDefaultCategories());
    setView("hidden");
  }, []);

  const handleSaveSettings = useCallback(() => {
    saveConsent(categories);
    setView("hidden");
  }, [categories]);

  const toggleCategory = (cat: ConsentCategory) => {
    if (cat === "necessary") return; // always on
    setCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  if (view === "hidden") return null;

  // ── Settings modal ──────────────────────────────────────────────
  if (view === "settings") {
    const categoryLabels: {
      key: ConsentCategory;
      label: string;
      desc: string;
      locked?: boolean;
    }[] = [
      {
        key: "necessary",
        label: "Notwendig",
        desc: "Immer aktiv. Technisch notwendige Funktionen und Speicherung der Consent-Auswahl.",
        locked: true,
      },
      {
        key: "statistics",
        label: "Statistik",
        desc: "Google Analytics – statistische Auswertung der Website-Nutzung.",
      },
      {
        key: "marketing",
        label: "Marketing",
        desc: "Google Ads und Meta Pixel – Messung von Werbeanzeigen und Zielgruppenbildung.",
      },
      {
        key: "external_media",
        label: "Externe Medien",
        desc: "YouTube-Videos (über youtube-nocookie.com mit Zwei-Klick-Lösung).",
      },
    ];

    return (
      <div
        className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-settings-title"
      >
        <div className="w-full max-w-[640px] max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl">
          <div className="sticky top-0 bg-white border-b border-gray-100 px-5 sm:px-7 py-4 flex items-center justify-between">
            <h2
              id="consent-settings-title"
              className="text-[18px] sm:text-[20px] font-bold text-primary"
            >
              Cookie-Einstellungen
            </h2>
            <button
              onClick={() => setView("hidden")}
              aria-label="Schließen"
              className="text-gray-400 hover:text-primary p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="px-5 sm:px-7 py-5 space-y-4">
            <p className="text-[14px] text-gray-600 leading-relaxed">
              Sie können jederzeit entscheiden, welche Kategorien Sie zulassen
              möchten. Notwendige Technologien können nicht deaktiviert werden,
              da sie für das Funktionieren der Website erforderlich sind.
            </p>

            {categoryLabels.map(({ key, label, desc, locked }) => (
              <div
                key={key}
                className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${
                  categories[key]
                    ? "border-secondary/40 bg-secondary/5"
                    : "border-gray-200 bg-gray-50/50"
                }`}
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={categories[key]}
                  aria-label={`${label} ${categories[key] ? "aktiviert" : "deaktiviert"}`}
                  disabled={locked}
                  onClick={() => toggleCategory(key)}
                  className={`mt-0.5 relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 ${
                    categories[key] ? "bg-secondary" : "bg-gray-300"
                  } ${locked ? "cursor-not-allowed opacity-70" : ""}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      categories[key] ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-bold text-primary">
                      {label}
                    </span>
                    {locked && (
                      <span className="text-[11px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                        immer aktiv
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-gray-500 mt-0.5 leading-relaxed">
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 sm:px-7 py-4 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleRejectAll}
              className="flex-1 rounded-xl border-2 border-gray-200 px-5 py-3 text-[15px] font-bold text-primary transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
            >
              Alle ablehnen
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 rounded-xl bg-secondary px-5 py-3 text-[15px] font-bold text-primary transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Alle akzeptieren
            </button>
            <button
              onClick={handleSaveSettings}
              className="flex-1 rounded-xl bg-primary px-5 py-3 text-[15px] font-bold text-white transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
            >
              Auswahl speichern
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Initial banner ──────────────────────────────────────────────
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9998] p-2 sm:p-3 animate-in slide-in-from-bottom duration-300"
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-banner-title"
    >
      <div className="mx-auto max-w-[1100px] rounded-2xl bg-primary shadow-2xl overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5 p-4 sm:p-5 lg:p-6">
          {/* Text */}
          <div className="flex-1 min-w-0">
            <h2
              id="consent-banner-title"
              className="text-[15px] sm:text-[16px] font-bold text-white mb-1"
            >
              Datenschutz & Cookies
            </h2>
            <p className="text-[12px] sm:text-[13px] text-white/70 leading-relaxed">
              Wir verwenden technisch notwendige Technologien sowie – nur nach
              Ihrer Einwilligung – Dienste für Statistik, Marketing und externe
              Medien.{" "}
              <a
                href="/datenschutz"
                className="underline text-secondary hover:text-secondary/80"
              >
                Datenschutzerklärung
              </a>
            </p>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 lg:shrink-0">
            <button
              onClick={handleRejectAll}
              className="rounded-lg border-2 border-white/30 px-4 py-2 text-[13px] font-bold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
            >
              Alle ablehnen
            </button>
            <button
              onClick={() => {
                const existing = getConsent();
                setCategories(existing?.categories ?? getDefaultCategories());
                setView("settings");
              }}
              className="rounded-lg border-2 border-white/30 px-4 py-2 text-[13px] font-bold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
            >
              Einstellungen
            </button>
            <button
              onClick={handleAcceptAll}
              className="rounded-lg bg-secondary px-4 py-2 text-[13px] font-bold text-primary transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Alle akzeptieren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
